/**
 * OPUS ci · idea 3 — Números generadores.
 *
 * El número generador es la hoja que demuestra de dónde sale la cantidad de cada renglón del presupuesto:
 * por pieza (IFC) o por planta/vista (DWG), con sus medidas, sus descuentos y el total que cuadra con la
 * hoja. Al asignar o medir un renglón se guarda una FOTO de la medición (`plano_concepto_meta.generador`):
 * el generador justifica el número que quedó en la hoja, no se recalcula al abrirlo.
 *
 * Aquí vive lo puro:
 *  - `fotoDeMedicion`: la foto de un elemento del plano medido con la unidad del concepto. Usa la MISMA
 *    cuenta que la hoja (`cantidadParaConcepto`) y la desglosa; si el desglose no da exactamente esa
 *    cantidad, queda una sola línea con la cantidad (nunca una foto que no cuadre).
 *  - `fotoDeRegla`: la de un complementario (sale de sus principales con una regla) o de lo que el plano
 *    no dibuja (un supuesto).
 *  - juntar / quitar elementos de una foto (un renglón por concepto: un renglón junta varios elementos).
 *  - `validarFotoGenerador`: lo que exige el servidor antes de guardarla.
 *  - `armarDocumentoGenerador`: el documento que se muestra e imprime (grupos, subtotales, cuadre).
 *
 * Nada de ejes ni fachadas inventados: la localización es el nombre de la pieza tal como viene más su
 * nivel/planta. Las reglas de los complementarios son las que ya dio la IA (`PlanoComplementario`), no
 * una tabla nueva.
 */
import type { PlanoComplementario, PlanoInputExtra, PlanoSeleccion } from './domain.js';
import { cantidadParaConcepto, esAreaDePiso, type EntradaCantidadParaConcepto } from './cantidadParaConcepto.js';
import { cuentaDeMuroConVanos, type VanoDelPlano } from './muroConVanos.js';
import { medidaPrincipalIfc } from './etiquetasIfc.js';

export type MagnitudGenerador = 'conteo' | 'lineal' | 'area' | 'volumen' | 'peso';

/** El elemento del plano de donde sale una línea (la misma identidad que la selección del renglón). */
export interface ElementoDelGenerador {
  tipo: 'capa' | 'bloque';
  clave: string;
  vista?: string;
}

export interface LineaGenerador {
  /** null = la línea no sale de un elemento dibujado (un supuesto de lo que el plano no dibuja). */
  elemento: ElementoDelGenerador | null;
  /** IFC: expressID de la pieza (para su croquis). */
  pieza?: string;
  /** Nivel (IFC) o planta/vista (DWG). '' = sin nivel. */
  grupo: string;
  /** Nombre de la pieza tal como viene en el plano (o de la capa/bloque). */
  localizacion: string;
  /** Texto corto debajo: el tipo del modelo, la regla, de dónde sale el alto. */
  detalle?: string;
  piezas: number | null;
  largo: number | null;
  ancho: number | null;
  alto: number | null;
  /** Con signo: un vano descontado es negativo. */
  parcial: number;
  clase: 'medida' | 'descuento' | 'regla' | 'supuesto';
  /** La medida no la declara el modelo: se midió del dibujo. */
  estimado?: boolean;
  /** Descuento de un muro con vanos: el tipo de vano (`claveDeTipoDeVano`), para volver a medir con su alto. */
  vano?: string;
  /**
   * Complementario: de qué medida del elemento de esta línea sale su parcial y con qué factor (el contrato que
   * dio la IA, ya resuelto). Con él «Cambió el plano» lo recalcula sin IA. Ausente = no se puede recalcular solo.
   * Vive en la LÍNEA (no en la foto) porque juntar fotos conserva solo los datos de la primera.
   */
  derivacion?: DerivacionDeComplementario;
}

/** El contrato con que un complementario sale de su principal: cantidad = medida(derivaDe) × factor. */
export interface DerivacionDeComplementario {
  derivaDe: Exclude<PlanoComplementario['derivaDe'], 'otro'>;
  factor: number;
  /** Solo con `derivaDe='largo'`: se mide con el largo CON los huecos (si el elemento los tiene medidos). */
  incluyeVanos?: boolean;
  /**
   * La medida que dice el propio concepto (m), si la cantidad pasó por ella (`porMedidaDelConcepto`): se aplica
   * después de derivar, con su propio redondeo.
   */
  medidaM?: number;
  /** La cantidad descansa en una medida que el plano no dibuja: al recalcularla queda «revisar», nunca sola. */
  supuesto?: boolean;
}

export interface FotoGenerador {
  /** 2 = las piezas IFC traen su cuenta (largo × alto − vanos); 1 = solo su parcial (renglones de antes). */
  version: 1 | 2;
  /**
   * 'plano' = medido de piezas dibujadas; 'complementario' = sale de sus principales con una regla;
   * 'no_dibujado' = supuesto de lo que el plano no dibuja; 'a_mano' = el plano no da la cantidad y la
   * escribió quien cotiza (sin líneas).
   */
  origen: 'plano' | 'complementario' | 'no_dibujado' | 'a_mano';
  magnitud: MagnitudGenerador | null;
  unidad: string | null;
  /** Decimales de la cantidad (2 en m/m²/kg, 3 en m³, 0 en piezas). */
  decimales: number;
  /** Lo que midió el plano (Σ de los parciales, redondeado como la hoja). */
  total: number;
  lineas: LineaGenerador[];
  /** Uso del elemento cuando el modelo lo dice (losa de azotea vs. de entrepiso): no se juntan entre usos. */
  uso?: string | null;
  /** DWG: el alto de muro con que se midió y de dónde salió. */
  altoUsado?: { valorM: number; deDonde: string } | null;
  espesorUsadoM?: number | null;
  /**
   * Qué pidió el concepto además de la medida (alto, espesor…): con la magnitud, es CÓMO se midió. «Cambió el plano»
   * lo usa para medir la revisión con los mismos ajustes que la hoja. Ausente en fotos de antes.
   */
  inputExtra?: PlanoInputExtra;
  /** Complementario: con qué regla sale de sus principales («Área del muro × 2»). */
  regla?: string | null;
  /** Lo que el plano no dibuja: de dónde sale el supuesto y con qué medidas del plano se estimó. */
  supuesto?: { deDonde: string; base: string[] } | null;
  nota?: string | null;
}

/** Identidad de un elemento (tipo + clave + vista), la misma que usa la hoja para sus renglones. */
export function claveDeElemento(e: { tipo: string; clave: string; vista?: string | null }): string {
  return `${e.tipo}:${e.clave}${e.vista ? `@${e.vista}` : ''}`;
}

const redondear = (n: number, dec: number): number => {
  const f = 10 ** dec;
  return Math.round(n * f) / f;
};
const r6 = (n: number): number => Math.round(n * 1e6) / 1e6;
const num = (s: string | undefined | null): number => Number((s ?? '').replace(',', '.'));

/**
 * Cuánto puede diferir la suma de los parciales del total: medio último decimal por cada elemento que se
 * redondeó por su lado (la hoja suma cantidades ya redondeadas de cada elemento) más un épsilon de coma
 * flotante. Es tolerancia de redondeo, no un umbral de negocio.
 */
export function toleranciaDeCuadre(decimales: number, partes: number): number {
  return Math.max(1, partes) * 0.5 * 10 ** -decimales + 1e-6;
}

/** Cuántos elementos distintos (redondeados por su lado) tiene la foto. */
function partesDe(lineas: readonly LineaGenerador[]): number {
  return new Set(lineas.map((l) => (l.elemento ? claveDeElemento(l.elemento) : `·${l.localizacion}`))).size;
}

const sumaParciales = (lineas: readonly LineaGenerador[]): number => lineas.reduce((s, l) => s + l.parcial, 0);

// ---------------------------------------------------------------------------------------------------
// La foto de un elemento medido
// ---------------------------------------------------------------------------------------------------

/** Una pieza del modelo IFC, ya con su nivel resuelto por quien llama. */
export interface PiezaIfcParaFoto {
  id: string;
  nombre: string;
  grupo: string;
  area?: number;
  longitud?: number;
  volumen?: number;
  peso?: number;
  /** Magnitudes que el modelo no declara (se midieron de la geometría). */
  estimadas?: readonly string[];
  /** Su caja en m: largo y ancho en planta, alto vertical (de la medición del visor). */
  dims?: { largo: number; ancho: number; alto: number };
  /** Las puertas y ventanas que llenan sus vanos (el modelo lo dice), con su ancho y alto. */
  vanos?: readonly VanoDePieza[];
}

/** Lo que llena un vano de una pieza: su nombre en singular («Ventana») y su ancho × alto en m. */
export interface VanoDePieza {
  nombre: string;
  ancho: number;
  alto: number;
}

const fmt2 = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Plural de un sustantivo en español (regla gramatical: vocal + s, consonante + es). */
const plural = (n: number, s: string) => (n === 1 ? s : /[aeiouáéíóú]$/i.test(s) ? `${s}s` : `${s}es`);

/** Los vanos agrupados por nombre y medida (para «2 ventanas de 2.00 × 1.20»). */
export function vanosPorTipo(vanos: readonly VanoDePieza[]): { nombre: string; ancho: number; alto: number; n: number }[] {
  const por = new Map<string, { nombre: string; ancho: number; alto: number; n: number }>();
  for (const v of vanos) {
    if (!(v.ancho > 0) || !(v.alto > 0)) continue;
    const k = `${v.nombre.toLowerCase()}|${fmt2(v.ancho)}|${fmt2(v.alto)}`;
    const g = por.get(k) ?? por.set(k, { nombre: v.nombre, ancho: v.ancho, alto: v.alto, n: 0 }).get(k)!;
    g.n++;
  }
  return [...por.values()];
}

/** «2 ventanas de 2.00 × 1.20 y 1 puerta de 1.01 × 2.01» (para leerlo en palabras). */
export function vanosEnPalabras(vanos: readonly VanoDePieza[]): string {
  const partes = vanosPorTipo(vanos).map((g) => `${g.n} ${plural(g.n, g.nombre.toLowerCase())} de ${fmt2(g.ancho)} × ${fmt2(g.alto)}`);
  return partes.length <= 1 ? partes.join('') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/**
 * Las líneas de UNA pieza IFC con la unidad del concepto, para que quien revisa rehaga la cuenta:
 *  - área/volumen: la cuenta de su caja (muro: largo × alto; losa: largo × ancho; volumen: × el tercero),
 *    menos los vanos que aloja como líneas negativas; si eso no da lo que mide el modelo (la forma de la
 *    pieza: piñones, encuentros), una línea con la diferencia, dicha como tal. El total es el del modelo.
 *  - largo: el largo, con su sección en palabras; piezas: 1; peso: el del modelo.
 * Sin caja (pieza sin geometría), una línea con la medida.
 */
export function lineasDePiezaIfc(
  p: PiezaIfcParaFoto,
  m: MagnitudGenerador,
  nombreTipo: string,
  linea: (x: Partial<LineaGenerador> & { parcial: number }) => LineaGenerador,
): LineaGenerador[] {
  const campo = m === 'lineal' ? 'longitud' : m === 'conteo' ? null : m;
  const valor = campo ? (p[campo] ?? 0) : 1;
  const estimado = campo != null && (p.estimadas ?? []).includes(campo);
  const base = { pieza: p.id, grupo: p.grupo, localizacion: p.nombre, ...(estimado ? { estimado: true } : {}) };
  const d = p.dims;
  const vanos = vanosPorTipo(p.vanos ?? []);
  const tipo = nombreTipo && nombreTipo !== p.nombre ? ` · tipo «${nombreTipo}»` : '';
  if (!d || !(d.largo > 0)) return [linea({ ...base, detalle: nombreTipo, piezas: 1, largo: m === 'lineal' ? valor : null, parcial: valor })];
  if (m === 'conteo') {
    return [linea({ ...base, detalle: `${fmt2(d.largo)} × ${fmt2(d.ancho)} × ${fmt2(d.alto)} m${tipo}`, piezas: 1, parcial: 1 })];
  }
  if (m === 'lineal') {
    // La sección son las otras dos medidas de su caja: en una pieza vertical (columna), su planta.
    const vertical = Math.abs(d.alto - valor) < Math.abs(d.largo - valor);
    const seccion = vertical ? [d.largo, d.ancho] : [d.ancho, d.alto];
    return [linea({ ...base, detalle: `${fmt2(valor)} m de largo, sección ${fmt2(seccion[0]!)} × ${fmt2(seccion[1]!)}${tipo}`, piezas: 1, largo: valor, parcial: valor })];
  }
  if (m === 'peso') {
    return [linea({ ...base, detalle: `${p.volumen != null ? `${fmt2(p.volumen)} m³ · ` : ''}peso del modelo${tipo}`, piezas: 1, parcial: valor })];
  }
  // Área o volumen: ¿pieza vertical (muro: largo × alto) u horizontal (losa: largo × ancho)? Lo que aloja
  // puertas o ventanas es vertical; si no, la cuenta que más se acerca a lo que mide el modelo.
  // El largo de un muro es el que mide el modelo por su eje (su caja en planta cuenta los encuentros de
  // las esquinas); sin él, el de la caja.
  const largoEje = p.longitud != null && p.longitud > 0 && p.longitud <= d.largo + 1e-6 ? p.longitud : d.largo;
  const vert = largoEje * d.alto;
  const horiz = d.largo * d.ancho;
  const area = p.area ?? 0;
  const esVertical = vanos.length > 0 || (m === 'area' ? Math.abs(vert - area) <= Math.abs(horiz - area) : d.alto >= d.ancho);
  const largo = esVertical ? largoEje : d.largo;
  const espesor = esVertical ? d.ancho : d.alto;
  const porEsp = m === 'volumen' ? espesor : 1;
  const bruto = (esVertical ? vert : horiz) * porEsp;
  const descuentos = vanos.map((g) => ({ g, m2: g.n * g.ancho * g.alto * porEsp }));
  const neto = bruto - descuentos.reduce((s, x) => s + x.m2, 0);
  const cuenta =
    `${fmt2(largo)} m de largo × ${fmt2(esVertical ? d.alto : d.ancho)} m de ${esVertical ? 'alto' : 'ancho'}` +
    (m === 'volumen' ? ` × ${fmt2(espesor)} m de ${esVertical ? 'espesor' : 'peralte'}` : '') +
    (vanos.length ? `, menos ${vanosEnPalabras(p.vanos ?? [])}` : '');
  const out: LineaGenerador[] = [
    linea({
      ...base,
      detalle: `${cuenta}${tipo}`,
      piezas: 1,
      largo,
      ...(esVertical ? { alto: d.alto, ancho: m === 'volumen' ? espesor : null } : { ancho: d.ancho, alto: m === 'volumen' ? espesor : null }),
      parcial: bruto,
    }),
    ...descuentos.map(({ g, m2 }) =>
      linea({
        ...base,
        clase: 'descuento',
        localizacion: `menos: ${g.n} ${plural(g.n, g.nombre.toLowerCase())} de ${fmt2(g.ancho)} × ${fmt2(g.alto)}`,
        detalle: undefined,
        piezas: g.n,
        largo: g.ancho,
        alto: g.alto,
        ancho: m === 'volumen' ? espesor : null,
        parcial: -m2,
      }),
    ),
  ];
  // Lo que el modelo mide de más o de menos que la cuenta de su caja (piñones, encuentros con otros muros):
  // se dice, no se esconde. El total de la pieza es el del modelo.
  const resto = valor - neto;
  // Una diferencia que no se ve al centésimo (coma flotante, redondeo del modelo) se queda en la línea de la
  // pieza: así la suma de la pieza es exactamente lo que mide el modelo, sin una línea de «0.00».
  if (Math.abs(resto) < 0.005) out[0] = { ...out[0]!, parcial: Math.round((out[0]!.parcial + resto) * 1e6) / 1e6 };
  else {
    out.push(
      linea({
        ...base,
        clase: resto < 0 ? 'descuento' : 'medida',
        localizacion: resto < 0 ? 'menos: forma de la pieza y encuentros' : 'más: forma de la pieza',
        detalle: `El modelo mide ${fmt2(valor)} ${m === 'volumen' ? 'm³' : 'm²'} ${estimado ? '(de su dibujo)' : '(lo declara)'}; la cuenta de su caja da ${fmt2(neto)}`,
        piezas: null,
        parcial: resto,
      }),
    );
  }
  return out;
}

/** Una planta (vista que suma) de un DWG con lo que mide ahí la capa. */
export interface PlantaParaFoto {
  vista: string;
  rotulo: string;
  largoM: number | null;
  altoM: number | null;
  deDondeAlto?: string;
  /** Área de recintos de la capa en esa planta (piso), m². */
  areaPisoM2?: number | null;
  /** Bloques: cuántas piezas hay en esa planta. */
  piezas?: number | null;
}

/** DWG: el muro con sus vanos, tramo por tramo (con el alto ya resuelto, el mismo de la cuenta de la hoja). */
export interface MuroParaFoto {
  tramos: { vista?: string; rotulo: string; ejeBrutoM: number; altoM: number; vanos: readonly VanoDelPlano[] }[];
  factor: number;
  altosPorTipo: Readonly<Record<string, number | null | undefined>>;
}

export interface EntradaFotoDeMedicion extends EntradaCantidadParaConcepto {
  elemento: ElementoDelGenerador;
  /** Cómo se llama el elemento (tipo del modelo, capa o bloque). */
  nombre: string;
  /** Grupo cuando no hay desglose (la planta elegida, el nivel del tipo). */
  grupo?: string;
  unidad: string | null;
  piezasIfc?: readonly PiezaIfcParaFoto[];
  plantas?: readonly PlantaParaFoto[];
  muro?: MuroParaFoto | null;
  /** De dónde salió el alto general (el que escribió quien cotiza, el que se leyó del corte…). */
  altoDeDonde?: string | null;
  uso?: string | null;
}

type MagIfc = 'conteo' | 'area' | 'longitud' | 'volumen';
const MAG_DE_IFC: Record<MagIfc, MagnitudGenerador> = { conteo: 'conteo', area: 'area', longitud: 'lineal', volumen: 'volumen' };
const DECIMALES: Record<MagnitudGenerador, number> = { conteo: 0, lineal: 2, area: 2, volumen: 3, peso: 2 };

/**
 * La foto de la medición de UN elemento con la unidad del concepto. null = el plano no da la cantidad
 * (la misma condición en que la hoja pide escribirla: falta el alto, el espesor, la escala…).
 */
export function fotoDeMedicion(e: EntradaFotoDeMedicion): FotoGenerador | null {
  const txt = cantidadParaConcepto(e);
  const total = num(txt);
  if (!txt || !Number.isFinite(total) || total <= 0) return null;
  const mag = e.magnitud?.magnitud;
  const alto = num(e.altoStr);
  const espesor = num(e.espesorStr);
  const linea = (p: Partial<LineaGenerador> & { parcial: number }): LineaGenerador => ({
    elemento: e.elemento,
    grupo: e.grupo ?? '',
    localizacion: e.nombre,
    piezas: null,
    largo: null,
    ancho: null,
    alto: null,
    clase: 'medida',
    ...p,
    parcial: r6(p.parcial),
  });
  let magnitud: MagnitudGenerador | null = null;
  let lineas: LineaGenerador[] | null = null;
  let altoUsado: FotoGenerador['altoUsado'] = null;
  let espesorUsadoM: number | null = null;
  const plantas = (e.plantas ?? []).filter((p) => p.largoM != null || p.areaPisoM2 != null || p.piezas != null);
  const cuadra = (xs: number[], objetivo: number, dec: number) =>
    xs.length > 0 && Math.abs(xs.reduce((s, x) => s + x, 0) - objetivo) <= toleranciaDeCuadre(dec, 1);

  if (e.medidas) {
    // IFC: la medida sale del modelo pieza por pieza.
    let m: MagnitudGenerador | null =
      mag === 'conteo' || mag === 'lineal' || mag === 'area' || mag === 'volumen' || mag === 'peso' ? mag : null;
    if (!m) {
      const mp = medidaPrincipalIfc({ ...e.medidas, manda: e.mandaIfc });
      m = mp ? MAG_DE_IFC[mp.magnitud as MagIfc] ?? null : null;
    }
    magnitud = m;
    const piezas = e.piezasIfc ?? [];
    if (m && piezas.length > 0) {
      const mm = m;
      lineas = piezas.flatMap((p) => lineasDePiezaIfc(p, mm, e.nombre, linea));
    }
  } else if (esAreaDePiso(e.magnitud, e.areaM2)) {
    magnitud = 'area';
    const conArea = plantas.filter((p) => (p.areaPisoM2 ?? 0) > 0);
    if (conArea.length > 1 && cuadra(conArea.map((p) => p.areaPisoM2!), total, 2)) {
      lineas = conArea.map((p) => linea({ grupo: p.rotulo, elemento: { ...e.elemento, vista: e.elemento.vista ?? p.vista }, parcial: p.areaPisoM2!, detalle: 'Área de los recintos cerrados de la capa' }));
    } else {
      lineas = [linea({ parcial: total, detalle: 'Área de los recintos cerrados de la capa' })];
    }
  } else if (e.netoVanosM2 != null && (mag === 'area' || mag === 'volumen') && e.muro && e.muro.tramos.length > 0) {
    // Muro con vanos: cada tramo bruto (largo × alto) y, debajo, sus vanos descontados.
    magnitud = mag;
    const porEsp = mag === 'volumen' ? espesor : 1;
    if (mag === 'volumen') espesorUsadoM = espesor;
    const ls: LineaGenerador[] = [];
    for (const t of e.muro.tramos) {
      const el = t.vista ? { ...e.elemento, vista: e.elemento.vista ?? t.vista } : e.elemento;
      ls.push(linea({ elemento: el, grupo: t.rotulo, largo: t.ejeBrutoM, alto: t.altoM, ancho: mag === 'volumen' ? espesor : null, parcial: t.ejeBrutoM * t.altoM * porEsp }));
      const c = cuentaDeMuroConVanos([t], e.muro.factor, e.muro.altosPorTipo);
      for (const d of c.descuentos) {
        const altoV = d.m2 / (d.anchoM * d.n);
        ls.push(
          linea({
            elemento: el,
            grupo: t.rotulo,
            clase: 'descuento',
            vano: d.clave,
            localizacion: `menos: ${d.n} ${d.bloque ? `«${d.bloque}»` : d.n === 1 ? 'hueco' : 'huecos'} de ${fmt(d.anchoM)} × ${fmt(altoV)}`,
            piezas: d.n,
            largo: d.anchoM,
            alto: altoV,
            ancho: mag === 'volumen' ? espesor : null,
            parcial: -d.m2 * porEsp,
          }),
        );
      }
    }
    lineas = ls;
  } else if ((mag === 'area' || mag === 'volumen') && e.areaMuroPorPlantas != null && plantas.length > 0) {
    magnitud = mag;
    const porEsp = mag === 'volumen' ? espesor : 1;
    if (mag === 'volumen') espesorUsadoM = espesor;
    lineas = plantas
      .filter((p) => p.largoM != null && p.altoM != null)
      .map((p) =>
        linea({
          grupo: p.rotulo,
          elemento: { ...e.elemento, vista: e.elemento.vista ?? p.vista },
          largo: p.largoM,
          alto: p.altoM,
          ancho: mag === 'volumen' ? espesor : null,
          detalle: p.deDondeAlto ? `Alto: ${p.deDondeAlto}` : undefined,
          // La hoja suma el área de cada planta ya redondeada.
          parcial: redondear(p.largoM! * p.altoM!, 2) * porEsp,
        }),
      );
  } else if (mag === 'area' || mag === 'volumen') {
    magnitud = mag;
    const porEsp = mag === 'volumen' ? espesor : 1;
    if (mag === 'volumen') espesorUsadoM = espesor;
    altoUsado = alto > 0 ? { valorM: alto, deDonde: e.altoDeDonde ?? '' } : null;
    const base = e.cantidadDefault;
    const conLargo = plantas.filter((p) => p.largoM != null);
    lineas =
      conLargo.length > 1 && cuadra(conLargo.map((p) => p.largoM!), base, 2)
        ? conLargo.map((p) =>
            linea({ grupo: p.rotulo, elemento: { ...e.elemento, vista: e.elemento.vista ?? p.vista }, largo: p.largoM, alto, ancho: mag === 'volumen' ? espesor : null, parcial: p.largoM! * alto * porEsp }),
          )
        : [linea({ largo: base, alto, ancho: mag === 'volumen' ? espesor : null, parcial: base * alto * porEsp })];
  } else {
    // Conteo de bloques o largo medido: la base del plano tal cual.
    const base = e.cantidadDefault;
    magnitud = e.baseEsConteo === true || mag === 'conteo' ? 'conteo' : mag === 'lineal' || e.baseEsConteo === false ? 'lineal' : null;
    if (magnitud === 'conteo') {
      const con = plantas.filter((p) => p.piezas != null && p.piezas > 0);
      lineas =
        con.length > 1 && cuadra(con.map((p) => p.piezas!), base, 0)
          ? con.map((p) => linea({ grupo: p.rotulo, piezas: p.piezas, parcial: p.piezas! }))
          : [linea({ piezas: base, parcial: base })];
    } else {
      const con = plantas.filter((p) => p.largoM != null && p.largoM > 0);
      lineas =
        con.length > 1 && cuadra(con.map((p) => p.largoM!), base, 2)
          ? con.map((p) => linea({ grupo: p.rotulo, elemento: { ...e.elemento, vista: e.elemento.vista ?? p.vista }, largo: p.largoM, parcial: p.largoM! }))
          : [linea({ largo: base, parcial: base })];
    }
  }

  const dec = magnitud ? DECIMALES[magnitud] : decimalesDe(total);
  // La foto nunca contradice a la hoja: si el desglose no da su cantidad, una sola línea con ella.
  if (!lineas || lineas.length === 0 || Math.abs(sumaParciales(lineas) - total) > toleranciaDeCuadre(dec, 1)) {
    lineas = [linea({ parcial: total, detalle: 'Toda la medida del elemento' })];
  }
  return {
    version: 2,
    origen: 'plano',
    magnitud,
    unidad: e.unidad,
    decimales: Math.max(dec, decimalesDe(total)),
    total,
    lineas,
    ...(e.uso ? { uso: e.uso } : {}),
    ...(altoUsado ? { altoUsado } : {}),
    ...(espesorUsadoM ? { espesorUsadoM } : {}),
    ...(e.magnitud?.inputExtra ? { inputExtra: e.magnitud.inputExtra } : {}),
  };
}

/** Los decimales que trae un número (hasta 6). */
function decimalesDe(n: number): number {
  for (let d = 0; d <= 6; d++) if (Math.abs(redondear(n, d) - n) < 1e-9) return d;
  return 6;
}

function fmt(n: number): string {
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------------------------------------------
// Complementarios y lo que el plano no dibuja
// ---------------------------------------------------------------------------------------------------

export interface LineaDeRegla {
  /** El elemento del principal del que sale (null en lo que el plano no dibuja). */
  elemento: ElementoDelGenerador | null;
  /** El principal del que sale («Muro exterior 30 cm»). */
  localizacion: string;
  grupo?: string;
  /** La operación, en palabras («Área del muro 120.00 m² × 2»). */
  detalle: string;
  parcial: number;
  /** Complementario: el contrato con que sale `parcial` de la medida del elemento (si se puede volver a calcular). */
  derivacion?: DerivacionDeComplementario;
}

/** La foto de un complementario (sale de sus principales) o de un supuesto de lo que el plano no dibuja. */
export function fotoDeRegla(e: {
  origen: 'complementario' | 'no_dibujado';
  unidad: string | null;
  total: number;
  lineas: readonly LineaDeRegla[];
  regla?: string | null;
  supuesto?: FotoGenerador['supuesto'];
}): FotoGenerador {
  return {
    version: 2,
    origen: e.origen,
    magnitud: null,
    unidad: e.unidad,
    // Los decimales que traen sus números (hasta 3): «4 pza», no «4.000 pza».
    decimales: Math.min(3, Math.max(decimalesDe(e.total), ...e.lineas.map((l) => decimalesDe(l.parcial)))),
    total: e.total,
    lineas: e.lineas.map((l) => ({
      elemento: l.elemento,
      grupo: l.grupo ?? '',
      localizacion: l.localizacion,
      detalle: l.detalle,
      piezas: null,
      largo: null,
      ancho: null,
      alto: null,
      parcial: r6(l.parcial),
      clase: e.origen === 'no_dibujado' ? 'supuesto' : 'regla',
      ...(e.origen === 'complementario' && l.derivacion ? { derivacion: l.derivacion } : {}),
    })),
    ...(e.regla ? { regla: e.regla } : {}),
    ...(e.supuesto ? { supuesto: e.supuesto } : {}),
  };
}

/** Un renglón sin medida del plano: la cantidad la escribió quien cotiza. */
export function fotoAMano(cantidad: number, unidad: string | null): FotoGenerador {
  return { version: 2, origen: 'a_mano', magnitud: null, unidad, decimales: decimalesDe(cantidad), total: cantidad, lineas: [] };
}

// ---------------------------------------------------------------------------------------------------
// Un renglón por concepto: juntar y quitar elementos
// ---------------------------------------------------------------------------------------------------

/** La foto de un renglón que junta dos (el mismo concepto en otro elemento). */
export function juntarFotos(a: FotoGenerador, b: FotoGenerador): FotoGenerador {
  const dec = Math.max(a.decimales, b.decimales);
  const origen = a.origen === b.origen ? a.origen : a.origen === 'a_mano' ? b.origen : a.origen;
  return {
    ...a,
    origen,
    magnitud: a.magnitud ?? b.magnitud,
    decimales: dec,
    total: redondear(a.total + b.total, dec),
    lineas: [...a.lineas, ...b.lineas],
    altoUsado: a.altoUsado ?? b.altoUsado ?? null,
    espesorUsadoM: a.espesorUsadoM ?? b.espesorUsadoM ?? null,
    regla: a.regla ?? b.regla ?? null,
    supuesto: a.supuesto ?? b.supuesto ?? null,
  };
}

/** Junta una lista de fotos; null si alguna falta (una parte sin foto deja el renglón sin foto). */
export function juntarTodas(fotos: readonly (FotoGenerador | null | undefined)[]): FotoGenerador | null {
  if (fotos.length === 0 || fotos.some((f) => !f)) return null;
  return (fotos as FotoGenerador[]).reduce((a, b) => juntarFotos(a, b));
}

/**
 * Quita de la foto las líneas de esos elementos (se reasignaron a otro renglón o se vuelven a medir).
 * `quitado` = lo que medían; el total baja eso.
 */
export function quitarElementosDeFoto(
  foto: FotoGenerador,
  claves: ReadonlySet<string>,
): { foto: FotoGenerador; quitado: number } {
  const salen = foto.lineas.filter((l) => l.elemento && claves.has(claveDeElemento(l.elemento)));
  if (salen.length === 0) return { foto, quitado: 0 };
  const quitado = sumaParciales(salen);
  return {
    foto: { ...foto, lineas: foto.lineas.filter((l) => !salen.includes(l)), total: redondear(foto.total - quitado, foto.decimales) },
    quitado,
  };
}

export type UnElemento = Omit<PlanoSeleccion, 'ademas'>;

/** Los elementos de una selección (la base y los de `ademas`). */
export function elementosDeSeleccion(sel: PlanoSeleccion | null | undefined): UnElemento[] {
  if (!sel) return [];
  const { ademas, ...base } = sel;
  return [base, ...(ademas ?? [])];
}

/** Una selección a partir de sus elementos (el primero es la identidad del renglón). */
export function seleccionDeElementos(els: readonly UnElemento[]): PlanoSeleccion | null {
  if (els.length === 0) return null;
  const [base, ...resto] = els;
  return resto.length ? { ...base!, ademas: [...resto] } : { ...base! };
}

// ---------------------------------------------------------------------------------------------------
// Identidad por PIEZAS (IFC): dos losas del mismo tipo son dos elementos distintos (AI-71)
// ---------------------------------------------------------------------------------------------------

/**
 * ¿El elemento se identifica por sus piezas? Solo una capa IFC con sus expressID (`ids`): el tipo es el
 * mismo para varias piezas y lo que distingue una losa de otra son sus ids. Un bloque, una capa de DWG o
 * un IFC viejo sin ids siguen identificándose por tipo y clave, como siempre.
 */
export function esPorPiezas(e: { tipo: string; clave?: string; ids?: readonly string[] | null }): boolean {
  return e.tipo === 'capa' && (e.ids?.length ?? 0) > 0;
}

/** ¿Los dos elementos comparten algo? Misma clave y, si ambos son por piezas, alguna pieza en común. */
export function seTocan(a: UnElemento, b: UnElemento): boolean {
  if (claveDeElemento(a) !== claveDeElemento(b)) return false;
  if (!esPorPiezas(a) || !esPorPiezas(b)) return true;
  const deB = new Set(b.ids);
  return a.ids!.some((id) => deB.has(id));
}

/**
 * Lo que le queda a `a` cuando salen los elementos `salen`. `resto` = null: `a` sale entero.
 * `idsQueSalen` = las piezas de `a` que salen (null = todo `a`). `indeterminado`: `a` no trae sus piezas
 * y sale solo una parte (el servidor no sabe cuánto restar): `a` se queda como está.
 */
export function restarPiezas(
  a: UnElemento,
  salen: readonly UnElemento[],
): { resto: UnElemento | null; idsQueSalen: string[] | null; indeterminado: boolean } {
  const tocan = salen.filter((s) => seTocan(a, s));
  if (tocan.length === 0) return { resto: a, idsQueSalen: [], indeterminado: false };
  // Sale todo `a` si alguno que lo toca no es por piezas, o si `a` mismo es del tipo entero y sale por piezas
  // (indeterminado).
  if (tocan.some((s) => !esPorPiezas(s))) return { resto: null, idsQueSalen: null, indeterminado: false };
  if (!esPorPiezas(a)) return { resto: a, idsQueSalen: [], indeterminado: true };
  const fuera = new Set(tocan.flatMap((s) => s.ids!));
  const quedan = a.ids!.filter((id) => !fuera.has(id));
  const salenIds = a.ids!.filter((id) => fuera.has(id));
  if (quedan.length === 0) return { resto: null, idsQueSalen: null, indeterminado: false };
  return { resto: { ...a, ids: quedan }, idsQueSalen: salenIds, indeterminado: false };
}

/** Un solo elemento por clave: junta sus piezas, sin repetir y en orden estable. */
export function compactarElementos(els: readonly UnElemento[]): UnElemento[] {
  const out = new Map<string, UnElemento>();
  for (const e of els) {
    const k = claveDeElemento(e);
    const ya = out.get(k);
    if (!ya) {
      out.set(k, e.ids ? { ...e, ids: [...new Set(e.ids)] } : { ...e });
      continue;
    }
    // Uno sin ids es el tipo entero: absorbe al que trae piezas.
    if (!ya.ids || !e.ids) {
      const { ids: _ids, ...sin } = ya;
      out.set(k, sin);
      continue;
    }
    out.set(k, { ...ya, ids: [...new Set([...ya.ids, ...e.ids])] });
  }
  return [...out.values()];
}

/**
 * Quita de la foto las líneas de esos elementos, pieza por pieza cuando salen por piezas. null = no se sabe
 * cuánto restar (una línea de esa clave no trae su pieza y lo que sale es una parte): quien llama deja el
 * renglón como está y lo marca para revisar.
 */
export function quitarPiezasDeFoto(
  foto: FotoGenerador,
  salen: readonly UnElemento[],
): { foto: FotoGenerador; quitado: number } | null {
  const lineasSalen: LineaGenerador[] = [];
  for (const l of foto.lineas) {
    if (!l.elemento) continue;
    const clave = claveDeElemento(l.elemento);
    const tocan = salen.filter((s) => claveDeElemento(s) === clave);
    if (tocan.length === 0) continue;
    if (tocan.some((s) => !esPorPiezas(s))) {
      lineasSalen.push(l);
      continue;
    }
    if (l.pieza == null) return null;
    if (tocan.some((s) => s.ids!.includes(l.pieza!))) lineasSalen.push(l);
  }
  if (lineasSalen.length === 0) return { foto, quitado: 0 };
  const quitado = sumaParciales(lineasSalen);
  return {
    foto: {
      ...foto,
      lineas: foto.lineas.filter((l) => !lineasSalen.includes(l)),
      total: redondear(foto.total - quitado, foto.decimales),
    },
    quitado,
  };
}

// ---------------------------------------------------------------------------------------------------
// Lo que exige el servidor
// ---------------------------------------------------------------------------------------------------

export type ValidacionFoto =
  | { ok: true; cantidadMedida: number | null }
  | { ok: false; motivo: string };

/**
 * ¿La foto se puede guardar con esta cantidad? Sus parciales tienen que dar su total (con la tolerancia
 * de redondeo de cada elemento) y su total tiene que ser la cantidad del renglón, salvo que quien cotiza
 * la haya escrito a mano (`aMano`): entonces la foto queda como «lo que midió el plano» (`cantidadMedida`).
 */
export function validarFotoGenerador(foto: FotoGenerador, cantidad: number, aMano = false): ValidacionFoto {
  if (!Number.isFinite(foto.total)) return { ok: false, motivo: 'La medición no trae su total.' };
  if (foto.origen === 'a_mano') {
    return foto.lineas.length === 0 ? { ok: true, cantidadMedida: null } : { ok: false, motivo: 'Una cantidad escrita a mano no lleva líneas.' };
  }
  if (foto.lineas.length === 0) return { ok: false, motivo: 'La medición no trae líneas.' };
  const suma = sumaParciales(foto.lineas);
  if (Math.abs(suma - foto.total) > toleranciaDeCuadre(foto.decimales, partesDe(foto.lineas))) {
    return { ok: false, motivo: `Las líneas de la medición suman ${redondear(suma, foto.decimales)} y su total dice ${foto.total}.` };
  }
  const igual = Math.abs(foto.total - cantidad) <= toleranciaDeCuadre(foto.decimales, 1);
  if (igual) return { ok: true, cantidadMedida: null };
  if (aMano) return { ok: true, cantidadMedida: foto.total };
  return { ok: false, motivo: `La medición da ${foto.total} y la cantidad del renglón es ${cantidad}.` };
}

// ---------------------------------------------------------------------------------------------------
// El documento
// ---------------------------------------------------------------------------------------------------

export interface GrupoDelGenerador {
  nombre: string;
  lineas: LineaGenerador[];
  subtotal: number;
}

export interface DocumentoGenerador {
  /** 'sin_foto' = renglón de antes de los generadores; 'a_mano' = el plano no da la cantidad. */
  estado: 'sin_foto' | 'a_mano' | 'ok';
  columnas: { piezas: boolean; largo: boolean; ancho: boolean; alto: boolean };
  grupos: GrupoDelGenerador[];
  /** Diferencia entre Σ de las líneas y el total, por redondear cada elemento por su lado. */
  ajusteRedondeo: number;
  /** Lo que midió el plano (el total de la foto). */
  medido: number | null;
  /** La cantidad de la hoja. */
  cantidadHoja: number;
  /** El total de la foto es lo que el plano midió para este renglón (✓). */
  cuadra: boolean;
  /** Quien cotiza cambió la cantidad: lo medido y lo que escribió. */
  corregidoAMano: { medido: number; aMano: number } | null;
  decimales: number;
  foto: FotoGenerador | null;
}

export const SIN_FOTO_GENERADOR =
  'Este renglón se agregó antes de que existieran los números generadores. Vuelve a agregarlo para verlo.';

/** El generador de un renglón tal como se muestra: agrupado por nivel/planta con sus subtotales. */
export function armarDocumentoGenerador(r: {
  cantidad: number;
  cantidadMedida?: number | null;
  generador?: FotoGenerador | null;
}): DocumentoGenerador {
  const foto = r.generador ?? null;
  const vacio: DocumentoGenerador = {
    estado: 'sin_foto',
    columnas: { piezas: false, largo: false, ancho: false, alto: false },
    grupos: [],
    ajusteRedondeo: 0,
    medido: null,
    cantidadHoja: r.cantidad,
    cuadra: false,
    corregidoAMano: null,
    decimales: decimalesDe(r.cantidad),
    foto,
  };
  if (!foto) return vacio;
  if (foto.origen === 'a_mano') return { ...vacio, estado: 'a_mano', medido: null, cuadra: false };
  const porGrupo = new Map<string, LineaGenerador[]>();
  for (const l of foto.lineas) (porGrupo.get(l.grupo) ?? porGrupo.set(l.grupo, []).get(l.grupo)!).push(l);
  const dec = foto.decimales;
  const grupos = [...porGrupo].map(([nombre, lineas]) => ({ nombre, lineas, subtotal: redondear(sumaParciales(lineas), dec) }));
  const sumaGrupos = grupos.reduce((s, g) => s + g.subtotal, 0);
  const medidoHoja = r.cantidadMedida ?? r.cantidad;
  const corregido = r.cantidadMedida != null && Math.abs(r.cantidadMedida - r.cantidad) > toleranciaDeCuadre(dec, 1);
  const hay = (k: 'piezas' | 'largo' | 'ancho' | 'alto') => foto.lineas.some((l) => l[k] != null);
  return {
    estado: 'ok',
    columnas: { piezas: hay('piezas'), largo: hay('largo'), ancho: hay('ancho'), alto: hay('alto') },
    grupos,
    ajusteRedondeo: redondear(foto.total - sumaGrupos, dec),
    medido: foto.total,
    cantidadHoja: r.cantidad,
    cuadra: Math.abs(foto.total - medidoHoja) <= toleranciaDeCuadre(dec, 1),
    corregidoAMano: corregido ? { medido: foto.total, aMano: r.cantidad } : null,
    decimales: dec,
    foto,
  };
}
